import { FullPageLayoutProfile, ITemplateField } from '../../types/damTypes';

export type FullPageLayoutGroup = 'productInfo' | 'recipeInfo' | 'wineInfo' | 'web' | 'wineWeb' | 'main';

export interface FullPageLayoutGroupOption {
  value: FullPageLayoutGroup;
  label: string;
}

export interface FullPageLayoutSection {
  id: string;
  title: string;
  fields: ITemplateField[];
  separator?: ITemplateField;
  isFirstSection: boolean;
  sourceIndex: number;
  order: number;
  layoutGroup: FullPageLayoutGroup;
  layoutPriority?: number;
}

export interface FullPageLayoutGroupBucket {
  value: FullPageLayoutGroup;
  label: string;
  sections: FullPageLayoutSection[];
}

const FULL_PAGE_LAYOUT_GROUPS: Record<FullPageLayoutProfile, FullPageLayoutGroupOption[]> = {
  product: [
    { value: 'productInfo', label: 'Produktinformation' },
    { value: 'web', label: 'Webb' },
  ],
  wine: [
    { value: 'wineInfo', label: 'Vin info' },
    { value: 'wineWeb', label: 'Web Vin' },
  ],
  recipe: [
    { value: 'recipeInfo', label: 'Recept info' },
    { value: 'web', label: 'Webb' },
  ],
  other: [{ value: 'main', label: 'Standard' }],
};

export const FULL_PAGE_LAYOUT_PROFILE_OPTIONS: Array<{
  value: FullPageLayoutProfile;
  label: string;
}> = [
  { value: 'product', label: 'Produkt' },
  { value: 'wine', label: 'Vin' },
  { value: 'recipe', label: 'Recept' },
  { value: 'other', label: 'Övrigt' },
];

export function normalizeFullPageLayoutProfile(value: unknown): FullPageLayoutProfile {
  return FULL_PAGE_LAYOUT_PROFILE_OPTIONS.some((option) => option.value === value)
    ? (value as FullPageLayoutProfile)
    : 'other';
}

export function getFullPageLayoutGroups(profile: FullPageLayoutProfile): FullPageLayoutGroupOption[] {
  return FULL_PAGE_LAYOUT_GROUPS[normalizeFullPageLayoutProfile(profile)];
}

export function getDefaultFullPageLayoutGroup(profile: FullPageLayoutProfile): FullPageLayoutGroup {
  return getFullPageLayoutGroups(profile)[0].value;
}

export function isValidFullPageLayoutGroup(
  profile: FullPageLayoutProfile,
  group: unknown,
): group is FullPageLayoutGroup {
  return getFullPageLayoutGroups(profile).some((option) => option.value === group);
}

export function normalizeFullPageLayoutGroup(
  profile: FullPageLayoutProfile,
  group: unknown,
): FullPageLayoutGroup {
  return isValidFullPageLayoutGroup(profile, group)
    ? group
    : getDefaultFullPageLayoutGroup(profile);
}

export function getFullPageLayoutGroupLabel(
  profile: FullPageLayoutProfile,
  group: unknown,
): string {
  const normalizedGroup = normalizeFullPageLayoutGroup(profile, group);
  return getFullPageLayoutGroups(profile).find((option) => option.value === normalizedGroup)?.label || 'Standard';
}

export function normalizeFullPageLayoutPriority(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

export function compareFullPageLayoutSections(
  a: FullPageLayoutSection,
  b: FullPageLayoutSection,
): number {
  const aPriority = a.layoutPriority;
  const bPriority = b.layoutPriority;

  if (aPriority !== undefined && bPriority === undefined) return -1;
  if (aPriority === undefined && bPriority !== undefined) return 1;
  if (aPriority !== undefined && bPriority !== undefined && aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  if (a.order !== b.order) return a.order - b.order;
  if (a.sourceIndex !== b.sourceIndex) return a.sourceIndex - b.sourceIndex;
  return a.id.localeCompare(b.id);
}

export function sortTemplateFieldsForDisplay(fields: ITemplateField[]): ITemplateField[] {
  return [...fields].sort((a, b) => {
    const orderA = a.order === undefined ? Infinity : a.order;
    const orderB = b.order === undefined ? Infinity : b.order;
    if (orderA === orderB) {
      return String(a.label || '').localeCompare(String(b.label || ''));
    }
    return orderA - orderB;
  });
}

export function splitFieldsIntoFullPageSections(
  fields: ITemplateField[],
  profile: FullPageLayoutProfile,
): FullPageLayoutSection[] {
  const sortedFields = sortTemplateFieldsForDisplay(fields);
  const sections: FullPageLayoutSection[] = [];
  let currentSection: FullPageLayoutSection | undefined;
  let isFirstSection = true;
  let sourceIndex = 0;

  sortedFields.forEach((field) => {
    if (field.type === 'separator') {
      if (currentSection && currentSection.fields.length > 0) {
        sections.push(currentSection);
      }

      currentSection = {
        id: field.id || `separator-${sourceIndex}`,
        title: field.label || 'Sektion',
        fields: [],
        separator: field,
        isFirstSection,
        sourceIndex,
        order: field.order === undefined ? sourceIndex : field.order,
        layoutGroup: normalizeFullPageLayoutGroup(profile, field.fullPageLayoutGroup),
        layoutPriority: normalizeFullPageLayoutPriority(field.fullPageLayoutPriority),
      };
      isFirstSection = false;
      sourceIndex += 1;
      return;
    }

    if (!currentSection) {
      currentSection = {
        id: 'leading-fields',
        title: 'Övrigt',
        fields: [],
        isFirstSection: false,
        sourceIndex,
        order: field.order === undefined ? sourceIndex : field.order,
        layoutGroup: getDefaultFullPageLayoutGroup(profile),
      };
      sourceIndex += 1;
    }

    currentSection.fields.push(field);
  });

  if (currentSection && currentSection.fields.length > 0) {
    sections.push(currentSection);
  }

  return sections;
}

export function groupFullPageLayoutSections(
  sections: FullPageLayoutSection[],
  profile: FullPageLayoutProfile,
): FullPageLayoutGroupBucket[] {
  const buckets = getFullPageLayoutGroups(profile).map((group) => ({
    ...group,
    sections: [] as FullPageLayoutSection[],
  }));
  const bucketByValue = new Map(buckets.map((bucket) => [bucket.value, bucket]));
  const defaultBucket = bucketByValue.get(getDefaultFullPageLayoutGroup(profile));

  sections.forEach((section) => {
    const group = normalizeFullPageLayoutGroup(profile, section.layoutGroup);
    const bucket = bucketByValue.get(group) || defaultBucket;
    bucket?.sections.push({
      ...section,
      layoutGroup: bucket.value,
    });
  });

  buckets.forEach((bucket) => {
    bucket.sections.sort(compareFullPageLayoutSections);
  });

  return buckets;
}
